import React, { useCallback, useEffect, useRef, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Text } from '../../components/ui'
import type { MarketingGame } from '../../types/marketing'
import {
  BOARD_SIZE,
  computePath,
  formatGameTime,
  generatePuzzle,
  getElapsedMs,
} from './gamePuzzle'

interface GameSimularModalProps {
  game: MarketingGame
  onClose: () => void
}

type Screen = 'start' | 'game' | 'result'

const GameSimularModal: React.FC<GameSimularModalProps> = ({ game, onClose }) => {
  const questions = game.questions ?? []
  const totalWords = questions.length

  const [screen, setScreen] = useState<Screen>('start')
  const [grid, setGrid] = useState<string[][] | null>(null)
  const [foundWords, setFoundWords] = useState<Record<string, boolean>>({})
  const [foundOrder, setFoundOrder] = useState<number[]>([])
  const [elapsedMs, setElapsedMs] = useState(0)
  const [resultMs, setResultMs] = useState(0)
  const [selectPath, setSelectPath] = useState<Array<[number, number]>>([])
  const [foundCells, setFoundCells] = useState<Set<string>>(new Set())

  const startTimeRef = useRef<number | null>(null)
  const timerRef = useRef<number | null>(null)
  const boardRef = useRef<HTMLDivElement | null>(null)
  const isSelectingRef = useRef(false)
  const selectStartRef = useRef<[number, number] | null>(null)
  const foundWordsRef = useRef<Record<string, boolean>>({})
  const foundOrderRef = useRef<number[]>([])
  const foundCellsRef = useRef<Set<string>>(new Set())

  const foundCount = Object.keys(foundWords).length

  const stopTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  useEffect(() => () => stopTimer(), [stopTimer])

  const startTimerNow = () => {
    stopTimer()
    const now = Date.now()
    startTimeRef.current = now
    setElapsedMs(0)
    timerRef.current = window.setInterval(() => {
      setElapsedMs(getElapsedMs(startTimeRef.current))
    }, 50)
  }

  const beginGame = () => {
    if (totalWords < 1) return
    const puzzle = generatePuzzle(questions.map((q) => q.word))
    setGrid(puzzle.grid)
    foundWordsRef.current = {}
    foundOrderRef.current = []
    foundCellsRef.current = new Set()
    setFoundWords({})
    setFoundOrder([])
    setFoundCells(new Set())
    setSelectPath([])
    startTimerNow()
    setScreen('game')
  }

  const finishGame = (order: number[]) => {
    stopTimer()
    const elapsed =
      startTimeRef.current != null ? getElapsedMs(startTimeRef.current) : elapsedMs
    setResultMs(elapsed)
    setFoundOrder(order)
    setScreen('result')
  }

  const markWordFound = (word: string, path: Array<[number, number]>) => {
    if (foundWordsRef.current[word]) return
    const questionIndex = questions.findIndex((q) => q.word === word)
    if (questionIndex < 0) return

    const nextFound = { ...foundWordsRef.current, [word]: true }
    const nextOrder = [...foundOrderRef.current, questionIndex + 1]
    foundWordsRef.current = nextFound
    foundOrderRef.current = nextOrder
    setFoundWords(nextFound)
    setFoundOrder(nextOrder)

    const nextCells = new Set(foundCellsRef.current)
    path.forEach(([r, c]) => nextCells.add(`${r},${c}`))
    foundCellsRef.current = nextCells
    setFoundCells(nextCells)

    if (Object.keys(nextFound).length >= totalWords) {
      finishGame(nextOrder)
    }
  }

  const evaluateSelection = (path: Array<[number, number]>) => {
    if (!grid || path.length === 0) return
    const letters = path.map(([r, c]) => grid[r][c]).join('')
    const reversed = letters.split('').reverse().join('')
    for (const q of questions) {
      if (foundWordsRef.current[q.word]) continue
      if (q.word === letters || q.word === reversed) {
        markWordFound(q.word, path)
        return
      }
    }
  }

  const pointerToCell = (clientX: number, clientY: number): [number, number] | null => {
    const target = document.elementFromPoint(clientX, clientY) as HTMLElement | null
    if (!target?.dataset?.r || !target?.dataset?.c) return null
    if (!target.classList.contains('game-cell-sim')) return null
    return [parseInt(target.dataset.r, 10), parseInt(target.dataset.c, 10)]
  }

  const onPointerDown = (e: React.PointerEvent) => {
    const cell = pointerToCell(e.clientX, e.clientY)
    if (!cell) return
    isSelectingRef.current = true
    selectStartRef.current = cell
    setSelectPath([cell])
    try {
      boardRef.current?.setPointerCapture(e.pointerId)
    } catch {
      /* ignore */
    }
    e.preventDefault()
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (!isSelectingRef.current || !selectStartRef.current) return
    const cell = pointerToCell(e.clientX, e.clientY)
    if (!cell) return
    const path = computePath(
      selectStartRef.current[0],
      selectStartRef.current[1],
      cell[0],
      cell[1]
    )
    if (path) setSelectPath(path)
    e.preventDefault()
  }

  const onPointerUp = () => {
    if (!isSelectingRef.current) return
    isSelectingRef.current = false
    const path = selectPath.slice()
    setSelectPath([])
    selectStartRef.current = null
    if (path.length > 0) evaluateSelection(path)
  }

  const selectPathSet = new Set(selectPath.map(([r, c]) => `${r},${c}`))

  const renderClues = (interactive: boolean) => (
    <ol className="space-y-2">
      {questions.map((q, idx) => {
        const found = !!foundWords[q.word]
        return (
          <li
            key={`${q.word}-${idx}`}
            className={`flex gap-3 rounded-lg border px-3 py-2 text-sm ${
              found
                ? 'border-teal-300 bg-teal-50 opacity-80 dark:border-teal-700 dark:bg-teal-900/30'
                : 'border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-900/40'
            }`}
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#008C9A] text-[11px] font-bold text-white">
              {idx + 1}
            </span>
            <span className="min-w-0">
              <span className={`text-gray-800 dark:text-gray-100 ${found ? 'line-through' : ''}`}>
                {q.clue}
              </span>
              {interactive && found && (
                <span className="mt-0.5 block text-[11px] font-bold text-[#016B76] dark:text-teal-300">
                  Palavra: {q.word}
                </span>
              )}
            </span>
          </li>
        )
      })}
    </ol>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 flex max-h-[95vh] w-full max-w-7xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700 sm:px-5">
          <div>
            <Heading as="h2" size="xl" className="text-gray-900 dark:text-white">
              Teste - {game.title}
            </Heading>
            <Text size="xs" color="muted" className="mt-0.5">
              Simulação: não grava ranking nem consome a tentativa do jogador.
            </Text>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
            aria-label="Fechar"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-4 sm:px-5">
          <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-center text-sm text-sky-900 dark:border-sky-700 dark:bg-sky-900/25 dark:text-sky-100">
            Modo simulação - use para validar as palavras e o tabuleiro.
          </div>

          {screen === 'start' && (
            <div className="mx-auto max-w-2xl">
              {game.description && (
                <Text size="sm" color="muted" className="mb-3 text-center">
                  {game.description}
                </Text>
              )}
              <p className="text-xs font-bold uppercase tracking-wide text-[#016B76] dark:text-teal-300">
                Dicas ({totalWords})
              </p>
              {renderClues(false)}
              <Button
                variant="solid"
                className="mt-6 w-full"
                onClick={beginGame}
                disabled={totalWords < 1}
              >
                Iniciar simulação
              </Button>
            </div>
          )}

          {screen === 'game' && grid && (
            <div className="mx-auto w-full max-w-6xl">
              <div className="mb-4 flex flex-wrap items-center justify-end gap-3">
                <div className="min-w-[96px] rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2 text-center dark:border-gray-600 dark:bg-gray-900/40">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
                    Tempo
                  </div>
                  <div className="text-xl font-extrabold tabular-nums text-[#016B76] dark:text-teal-300">
                    {formatGameTime(elapsedMs)}
                  </div>
                </div>
                <div className="min-w-[96px] rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2 text-center dark:border-gray-600 dark:bg-gray-900/40">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
                    Encontradas
                  </div>
                  <div className="text-xl font-extrabold tabular-nums text-[#016B76] dark:text-teal-300">
                    {foundCount}/{totalWords}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(260px,1fr)_minmax(0,1.4fr)] lg:items-start">
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-wide text-[#016B76] dark:text-teal-300">
                    Dicas
                  </p>
                  <div className="mt-2 max-h-[min(65vh,560px)] overflow-y-auto overscroll-contain rounded-lg border border-gray-200 bg-gray-50/80 p-2 pr-1 dark:border-gray-600 dark:bg-gray-900/40 [scrollbar-gutter:stable]">
                    {renderClues(true)}
                  </div>
                </div>

                <div className="min-w-0">
                  <div className="flex justify-center overflow-x-auto p-1">
                    <div
                      ref={boardRef}
                      className="grid touch-none select-none overflow-hidden rounded-[10px] border-2 border-[#008C9A]"
                      style={{
                        gridTemplateColumns: `repeat(${BOARD_SIZE}, min(36px, 5.8vw))`,
                        gridTemplateRows: `repeat(${BOARD_SIZE}, min(36px, 5.8vw))`,
                        gap: '1px',
                        background: '#DCE7E8',
                      }}
                      onPointerDown={onPointerDown}
                      onPointerMove={onPointerMove}
                      onPointerUp={onPointerUp}
                      onPointerCancel={onPointerUp}
                    >
                      {grid.map((row, r) =>
                        row.map((letter, c) => {
                          const key = `${r},${c}`
                          const isFound = foundCells.has(key)
                          const isSelecting = selectPathSet.has(key) && !isFound
                          return (
                            <div
                              key={key}
                              data-r={r}
                              data-c={c}
                              className={`game-cell-sim flex items-center justify-center font-bold ${
                                isFound
                                  ? 'bg-[#CFF1F0] text-[#016B76] shadow-[inset_0_0_0_2px_#01AAB3]'
                                  : isSelecting
                                    ? 'bg-[#FFD873] shadow-[inset_0_0_0_2px_#E7A900]'
                                    : 'bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100'
                              }`}
                              style={{
                                fontSize: 'calc(min(36px, 5.8vw) * 0.5)',
                                cursor: 'pointer',
                              }}
                            >
                              {letter}
                            </div>
                          )
                        })
                      )}
                    </div>
                  </div>
                  <p className="mt-3 text-center text-sm text-gray-500 dark:text-gray-400">
                    Arraste sobre as letras para marcar cada palavra.
                  </p>
                </div>
              </div>
            </div>
          )}

          {screen === 'result' && (
            <div className="mx-auto max-w-2xl text-center">
              <Text size="sm" color="muted">
                Simulação concluída em
              </Text>
              <div className="mt-1 text-4xl font-extrabold tabular-nums text-[#016B76] dark:text-teal-300">
                {formatGameTime(resultMs)}
              </div>
              <Text size="sm" color="muted" className="mt-2">
                Ordem: {foundOrder.join(' → ') || '—'}
              </Text>
              <div className="mt-6 flex flex-col gap-2 sm:flex-row">
                <Button variant="solid" className="flex-1" onClick={beginGame}>
                  Simular novamente
                </Button>
                <Button variant="outline" className="flex-1" onClick={onClose}>
                  Fechar
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default GameSimularModal
